import type { TFunction } from "i18next";
import { useState, useRef, useEffect, useMemo, useCallback } from "react";
import { useTranslation } from "react-i18next";
import U2000SaveCancelDialog from "../../../../u2000Core/components/dialog/U2000SaveCancelDialog";
import U2000InputText from "../../../../u2000Core/components/formControls/input/U2000InputText";
import U2000InputMask from "../../../../u2000Core/components/formControls/input/U2000InputMask";
import U2000AutoCompleteInput from "../../../../u2000Core/components/formControls/autoComplete/U2000AutoCompleteInput";
import U2000ComboBox from "../../../../u2000Core/components/formControls/combobox/U2000ComboBox";
import U2000CheckBox from "../../../../u2000Core/components/formControls/checkbox/U2000CheckBox";
import { useToast } from "../../../../u2000Core/context/toast/U2000ToastContext";
import { isValidId } from "../../../../u2000Core/tools/U2000IdUtils";
import { type U3120CustomerType, U3120ContactPhoneType, type U3120QuickCustomerCreatePayload } from "../../../../u3120Customer/U3120Service";
import {
  useU3121CreateQuickCustomerHandlers,
  useU3121FindCountriesHandlers,
  useU3121FindStatesHandlers,
} from "../../../U3121UseFindHandlers";
import { CUSTOMER_LANGUAGE_TYPES } from "../../../../u3120Customer/components/common/customerLanguageTypes";
import { useU3121Context } from "../../../U3121Context";
import { NS as U3121_NS } from "../../../U3121";
import type { U3121Customer } from "../../../U3121Service";

export interface U3121NewCustomerDlgProps {
  isOpen: boolean;
  onClose: (action: "save" | "cancel") => void;
  onSuccess: (customer: U3121Customer) => void;
  t: TFunction;
}

const emptyForm: U3120QuickCustomerCreatePayload = {
  customerName: "",
  contactName: "",
  customerType: null,
  isRegularCustomer: false,
  address: "",
  city: "",
  postalCode: "",
  officePhone1: "",
  officePhone1Type: null,
  officePhone1Extension: null,
  officePhone2: "",
  officePhone2Type: null,
  officePhone2Extension: null,
  language: "",
  notes: "",
  email: "",
  country: null,
  state: null,
};

function U3121NewCustomerDlg({ isOpen, onClose, onSuccess, t }: U3121NewCustomerDlgProps) {
  const { i18n } = useTranslation();
  const toast = useToast();
  const { initLU } = useU3121Context();
  const { search: searchCountries } = useU3121FindCountriesHandlers();
  const { search: searchStates } = useU3121FindStatesHandlers();
  const { createQuickCustomer } = useU3121CreateQuickCustomerHandlers();
  const customerNameRef = useRef<HTMLInputElement>(null);

  const [form, setForm] = useState<U3120QuickCustomerCreatePayload>(emptyForm);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);

  const customerTypeOptions = useMemo(() => {
    const types = (initLU.customerTypes ?? []) as U3120CustomerType[];
    return types.map((item) => ({
      ...item,
      label: i18n.language === "fr" ? (item.customerTypeNameFr ?? item.customerTypeCode ?? "") : (item.customerTypeNameEn ?? item.customerTypeCode ?? ""),
    }));
  }, [initLU.customerTypes, i18n.language]);

  const languageOptions = useMemo(
    () =>
      CUSTOMER_LANGUAGE_TYPES.map((type) => ({
        value: type.value,
        label: typeof type.label === "object" ? (i18n.language === "fr" ? type.label.fr : type.label.en) : String(type.label),
      })),
    [i18n.language],
  );

  const phoneTypeOptions = useMemo(
    () => [
      { value: U3120ContactPhoneType.OFFICE, label: t("lblPhoneTypeOffice", { ns: U3121_NS }) },
      { value: U3120ContactPhoneType.HOME, label: t("lblPhoneTypeHome", { ns: U3121_NS }) },
      { value: U3120ContactPhoneType.CELLPHONE, label: t("lblPhoneTypeCellphone", { ns: U3121_NS }) },
    ],
    [t],
  );

  useEffect(() => {
    if (isOpen) {
      const types = (initLU.customerTypes ?? []) as U3120CustomerType[];
      const particulierType = types.find((ty) => (ty.customerTypeCode ?? "").toUpperCase() === "PAR");
      const defaultTypeId = particulierType?.id ?? (types.length > 0 ? types[0].id : null);
      const defaultLang = "fr";
      setForm({
        ...emptyForm,
        customerType: defaultTypeId,
        language: defaultLang,
        isRegularCustomer: false,
        country: initLU.defaultCountry?.[0] ?? null,
        state: initLU.defaultState?.[0] ?? null,
      });
      setErrors({});
    }
  }, [isOpen, initLU.customerTypes, initLU.defaultCountry, initLU.defaultState]);

  const officePhone1Type = form.officePhone1Type;
  const officePhone2Type = form.officePhone2Type;

  const currentCustomerType = useMemo(() => {
    const types = (initLU.customerTypes ?? []) as U3120CustomerType[];
    const id = typeof form.customerType === "object" && form.customerType !== null && "id" in form.customerType ? (form.customerType as U3120CustomerType).id : form.customerType;
    if (id == null || id === "") return null;
    return types.find((ty) => ty.id === id) ?? null;
  }, [initLU.customerTypes, form.customerType]);

  const isCommercialType = (currentCustomerType?.customerTypeCode ?? "").toUpperCase() === "CORM";
  const showContactName = isCommercialType;

  useEffect(() => {
    setForm((prev) => {
      let next = prev;
      if (prev.officePhone1Type !== U3120ContactPhoneType.OFFICE && prev.officePhone1Extension != null && prev.officePhone1Extension !== "") {
        next = { ...next, officePhone1Extension: null };
      }
      if (prev.officePhone2Type !== U3120ContactPhoneType.OFFICE && prev.officePhone2Extension != null && prev.officePhone2Extension !== "") {
        next = { ...next, officePhone2Extension: null };
      }
      return next;
    });
  }, [officePhone1Type, officePhone2Type]);

  const setByPath = (obj: Record<string, unknown>, path: string, value: unknown): Record<string, unknown> => {
    const cloned = JSON.parse(JSON.stringify(obj));
    const parts = path.split(".");
    let cur: Record<string, unknown> = cloned;
    for (let i = 0; i < parts.length - 1; i++) {
      const p = parts[i];
      if (cur[p] == null || typeof cur[p] !== "object") cur[p] = {};
      cur = cur[p] as Record<string, unknown>;
    }
    cur[parts[parts.length - 1]] = value;
    return cloned;
  };

  const isFormValid = (): boolean => {
    if (!form.customerName?.trim()) return false;
    if (customerTypeOptions.length > 0) {
      const ct = form.customerType;
      if (!ct || (typeof ct === "string" && !ct.trim()) || (typeof ct === "object" && (!ct.id || !ct.id.trim()))) return false;
    }
    if (!form.language?.trim()) return false;
    return true;
  };

  const validate = (): boolean => {
    const next: Record<string, string> = {};
    if (!form.customerName?.trim()) next.customerName = t("errCustomerNameRequired", { ns: U3121_NS });
    if (customerTypeOptions.length > 0) {
      const ct = form.customerType;
      if (!ct || (typeof ct === "string" && !ct.trim()) || (typeof ct === "object" && (!ct.id || !ct.id.trim()))) {
        next["customerType.id"] = t("errCustomerTypeRequired", { ns: U3121_NS });
      }
    }
    if (!form.language?.trim()) next.language = t("errLanguageRequired", { ns: U3121_NS });
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSave = async () => {
    if (!validate()) return;
    setSaving(true);
    try {
      const payload = { ...form };
      if (!payload.contactName?.trim()) {
        payload.contactName = payload.customerName ?? "";
      }
      const types = (initLU.customerTypes ?? []) as U3120CustomerType[];
      if (typeof payload.customerType === "string") {
        payload.customerType = types.find((ty) => ty.id === payload.customerType) ?? null;
      }
      const resolvedType = typeof payload.customerType === "object" && payload.customerType !== null ? payload.customerType : types.find((ty) => ty.id === payload.customerType);
      if ((resolvedType?.customerTypeCode ?? "").toUpperCase() === "PAR") {
        payload.contactName = payload.customerName ?? "";
      }
      const result = await createQuickCustomer(payload);
      if (result.ok) {
        const created = result.created as {
          id?: string;
          customerCode?: string;
          customerName?: string;
          officePhone1?: string;
          officePhone2?: string;
        };
        if (created?.id) {
          const customer: U3121Customer = {
            id: created.id,
            customerCode: created.customerCode,
            customerName: created.customerName ?? form.customerName,
            officePhone1: created.officePhone1 ?? form.officePhone1,
            officePhone2: created.officePhone2 ?? form.officePhone2,
          };
          onSuccess(customer);
          onClose("save");
        } else {
          toast.error(t("errUnexpectedResponse", { ns: U3121_NS }), t("successTitle", { ns: U3121_NS }));
        }
      } else if (result.fieldErrors && Object.keys(result.fieldErrors).length > 0) {
        setErrors((prev) => ({ ...prev, ...result.fieldErrors }));
      }
    } finally {
      setSaving(false);
    }
  };

  const handleClose = (action: "save" | "cancel") => {
    if (action === "save") {
      handleSave();
    } else {
      onClose("cancel");
    }
  };

  const countryResultFields = i18n.language === "fr" ? ["countryNameFr"] : i18n.language === "en" ? ["countryNameEn"] : ["countryName"];
  const stateResultFields = i18n.language === "fr" ? ["stateNameFr"] : i18n.language === "en" ? ["stateNameEn"] : ["stateName"];

  const displayCountry = (item: Record<string, unknown>) => {
    if (i18n.language === "fr" && item.countryNameFr) return String(item.countryNameFr);
    if (i18n.language === "en" && item.countryNameEn) return String(item.countryNameEn);
    return String(item.countryName ?? item.name ?? "");
  };

  const extractCountryData = (result: Record<string, unknown> | null) => {
    if (result) {
      return [
        { fieldName: "country.id", value: result.id },
        { fieldName: "country.countryCode", value: result.countryCode },
        { fieldName: "country.countryNameFr", value: result.countryNameFr },
        { fieldName: "country.countryNameEn", value: result.countryNameEn },
      ];
    }
    return [
      { fieldName: "country.id", value: null },
      { fieldName: "country.countryCode", value: null },
      { fieldName: "country.countryNameFr", value: null },
      { fieldName: "country.countryNameEn", value: null },
    ];
  };

  const displayState = (item: Record<string, unknown>) => {
    if (i18n.language === "fr" && item.stateNameFr) return String(item.stateNameFr);
    if (i18n.language === "en" && item.stateNameEn) return String(item.stateNameEn);
    return String(item.stateName ?? item.name ?? "");
  };

  const extractStateData = (result: Record<string, unknown> | null) => {
    if (result) {
      return [
        { fieldName: "state.id", value: result.id },
        { fieldName: "state.stateCode", value: result.stateCode },
        { fieldName: "state.stateNameFr", value: result.stateNameFr },
        { fieldName: "state.stateNameEn", value: result.stateNameEn },
      ];
    }
    return [
      { fieldName: "state.id", value: null },
      { fieldName: "state.stateCode", value: null },
      { fieldName: "state.stateNameFr", value: null },
      { fieldName: "state.stateNameEn", value: null },
    ];
  };

  const onSearchCountry = useCallback(
    async (criterias: { fieldName: string; value: unknown }[]) => searchCountries(criterias),
    [searchCountries]
  );

  const onSearchState = useCallback(
    async (criterias: { fieldName: string; value: unknown }[]) => {
      const next = [...criterias];
      if (form.country?.id && isValidId(form.country.id)) {
        next.push({ fieldName: "countryId", value: form.country.id });
      }
      return searchStates(next);
    },
    [searchStates, form.country?.id]
  );

  const formStateHandlers = {
    getData: () => form,
    setData: (data: U3120QuickCustomerCreatePayload) => {
      setForm((prev) => {
        const next = data ?? prev;
        if (next && (!next.country?.id || !isValidId(next.country.id)) && next.state) {
          return { ...next, state: null };
        }
        return next;
      });
    },
    getErrors: () => errors,
    setErrors,
    getTouched: () => ({}),
    getFormState: () => "EDIT",
    getFormEditMode: () => "ADD",
    isSubmitted: () => false,
    setSubmitted: () => {},
    onChange: (e: unknown, value: string | number | boolean) => {
      const id = (e as React.ChangeEvent<HTMLInputElement>)?.target?.id;
      if (id) setForm((prev) => setByPath(prev as Record<string, unknown>, id, value) as U3120QuickCustomerCreatePayload);
    },
    onBlur: () => {},
  } as unknown as import("../../../../u2000Core/reducer/formReducer/U2000FormStateHandlers").U2000FormStateHandlers<U3120QuickCustomerCreatePayload>;

  return (
    <U2000SaveCancelDialog
      isOpen={isOpen}
      onClose={handleClose}
      title={t("lblNewCustomer", { ns: U3121_NS })}
      isValid={isFormValid() && !saving}
      readOnly={false}
      defaultFocusRef={customerNameRef as React.RefObject<HTMLElement>}
    >
      <div className="flex flex-col gap-y-4">
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <U2000ComboBox
            formStateHandlers={formStateHandlers}
            fieldName="customerType"
            label={t("lblCustomerType", { ns: U3121_NS })}
            data={customerTypeOptions}
            keyField="id"
            descField="label"
            getValue={(item) => item.id}
            findItemByValue={(value, data) => {
              if (value == null || value === "") return null;
              const id = typeof value === "object" && value !== null && "id" in value ? (value as U3120CustomerType).id : String(value);
              return data.find((item) => item.id === id) || null;
            }}
            required
          />
          <U2000ComboBox
            formStateHandlers={formStateHandlers}
            fieldName="language"
            label={t("lblLanguage", { ns: U3121_NS })}
            data={languageOptions}
            keyField="value"
            descField="label"
            getValue={(item) => item.value}
            findItemByValue={(value, data) => data.find((item) => item.value === value) || null}
            required
          />
        </div>
        <div className="flex items-center gap-4">
          <div className="min-w-0 flex-1">
            <U2000InputText
              fieldName="customerName"
              label={t("lblCustomerName", { ns: U3121_NS })}
              formStateHandlers={formStateHandlers}
              inputRef={customerNameRef as React.RefObject<HTMLInputElement>}
              required
            />
          </div>
          <div className="flex shrink-0 items-center self-center pt-6">
            <U2000CheckBox
              formStateHandlers={formStateHandlers}
              fieldName="isRegularCustomer"
              label={t("lblIsRegularCustomer", { ns: U3121_NS })}
            />
          </div>
        </div>
        {showContactName && <U2000InputText fieldName="contactName" label={t("lblContactName", { ns: U3121_NS })} formStateHandlers={formStateHandlers} />}
        <U2000InputText fieldName="address" label={t("lblAddress", { ns: U3121_NS })} formStateHandlers={formStateHandlers} />
        <div className="grid grid-cols-[3fr_1fr] gap-4">
          <U2000InputText fieldName="city" label={t("lblCity", { ns: U3121_NS })} formStateHandlers={formStateHandlers} />
          <U2000InputMask fieldName="postalCode" label={t("lblPostalCode", { ns: U3121_NS })} mask="postalCodeCA" formStateHandlers={formStateHandlers} />
        </div>
        <div className="grid grid-cols-2 gap-4">
          <U2000AutoCompleteInput
            formStateHandlers={formStateHandlers}
            fieldName="country"
            mainFieldName="countryNameFr"
            label={t("lblCountry", { ns: U3121_NS })}
            onSearch={onSearchCountry}
            displayFn={displayCountry}
            overrideExtractDataFn={extractCountryData}
            minCarLaunchSearch={0}
            resultLabels={[]}
            resultFields={countryResultFields}
          />
          <U2000AutoCompleteInput
            formStateHandlers={formStateHandlers}
            fieldName="state"
            mainFieldName="stateNameFr"
            label={t("lblState", { ns: U3121_NS })}
            onSearch={onSearchState}
            displayFn={displayState}
            overrideExtractDataFn={extractStateData}
            minCarLaunchSearch={0}
            resultLabels={[]}
            resultFields={stateResultFields}
            readonly={!form.country?.id || !isValidId(form.country.id)}
          />
        </div>
        <div className="grid grid-cols-[1fr_1fr_auto] gap-4">
          <U2000InputMask fieldName="officePhone1" label={t("lblOfficePhonePrincipal1", { ns: U3121_NS })} mask="phone" formStateHandlers={formStateHandlers} />
          <U2000ComboBox
            formStateHandlers={formStateHandlers}
            fieldName="officePhone1Type"
            label={t("lblPhoneType", { ns: U3121_NS })}
            data={phoneTypeOptions}
            keyField="value"
            descField="label"
            getValue={(item) => item.value}
            findItemByValue={(value, data) => data.find((item) => item.value === value) || null}
          />
          <div className="min-w-[100px]">
            <U2000InputText
              fieldName="officePhone1Extension"
              label={t("lblPhone1Extension", { ns: U3121_NS })}
              formStateHandlers={formStateHandlers}
              readonly={officePhone1Type !== U3120ContactPhoneType.OFFICE}
            />
          </div>
        </div>
        <div className="grid grid-cols-[1fr_1fr_auto] gap-4">
          <U2000InputMask fieldName="officePhone2" label={t("lblOfficePhonePrincipal2", { ns: U3121_NS })} mask="phone" formStateHandlers={formStateHandlers} />
          <U2000ComboBox
            formStateHandlers={formStateHandlers}
            fieldName="officePhone2Type"
            label={t("lblPhoneType", { ns: U3121_NS })}
            data={phoneTypeOptions}
            keyField="value"
            descField="label"
            getValue={(item) => item.value}
            findItemByValue={(value, data) => data.find((item) => item.value === value) || null}
          />
          <div className="min-w-[100px]">
            <U2000InputText
              fieldName="officePhone2Extension"
              label={t("lblPhone2Extension", { ns: U3121_NS })}
              formStateHandlers={formStateHandlers}
              readonly={officePhone2Type !== U3120ContactPhoneType.OFFICE}
            />
          </div>
        </div>
        <U2000InputText fieldName="email" label={t("lblEmail", { ns: U3121_NS })} formStateHandlers={formStateHandlers} />
        <U2000InputText fieldName="notes" label={t("lblNotes", { ns: U3121_NS })} formStateHandlers={formStateHandlers} />
      </div>
    </U2000SaveCancelDialog>
  );
}

export default U3121NewCustomerDlg;
